import { getRulebookProfile, type RulebookProfile as RulebookProfileData, useUserInfo } from "@next-platform/client-common-core";
import { Spinner } from "@next-platform/client-common-spicy-ui";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Col, PageLayout, PageSize } from "@/components";
import PageMetaTags from "@/components/common/PageMetaTags";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import AnonymousMarketing from "@/pages/AnonymousMarketing/AnonymousMarketing";
import useRulebookDraftOwner from "@/pages/RulebookEditor/hooks/useRulebookDraftOwner";
import useAuth from "@/services/useAuth";

import RulebookBotsSection from "./RulebookBotsSection";
import RulebookCombinedRulesSection from "./RulebookCombinedRulesSection";
import RulebookDefinitionMetersSection from "./RulebookDefinitionMetersSection";
import RulebookMetersSection from "./RulebookMetersSection";
import RulebookProfileHeader from "./RulebookProfileHeader";
import RulebookStartValuesSection from "./RulebookStartValuesSection";

/** Definition-aware sections render only when the profile embeds the shared definition. */
export const RulebookProfileSections = ({ profile, botsSection }: { profile: RulebookProfileData; botsSection?: ReactNode }) => (
  <Col className="gap-6" data-testid="RulebookProfileSections">
    {profile.definition ? (
      <>
        <RulebookStartValuesSection meters={profile.definition.meters} />
        <RulebookDefinitionMetersSection meters={profile.definition.meters} />
        <RulebookCombinedRulesSection rules={profile.definition.combined_rules} meters={profile.definition.meters} />
      </>
    ) : (
      <RulebookMetersSection meters={profile.meters} hasHiddenMeters={profile.hasHiddenMeters} />
    )}
    {botsSection ?? <RulebookBotsSection rulebookId={profile.id} />}
  </Col>
);

/**
 * Public Rulebook profile (`/rulebook/:id`). Signed-out visitors get the marketing landing —
 * the GET needs a session anyway. A 404 (missing, foreign private, unapproved) renders the
 * shared notFound meta like LorebookDetail.
 */
const RulebookProfile = () => {
  const { id: rulebookId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { onlyUserId } = useUserInfo();
  // Scope the draft store to this account before Edit/Clone can write to it.
  const isDraftOwnerReady = useRulebookDraftOwner(onlyUserId);
  // The anonymous store reports ready before the signed-in id arrives — a clone/import would land
  // in the memory store that the later owner switch wipes.
  const isDraftStoreScoped = isDraftOwnerReady && !!onlyUserId;
  const [stateProfile, setStateProfile] = useState<RulebookProfileData | null>(null);
  const [stateIsLoading, setStateIsLoading] = useState(true);
  const [stateIsError, setStateIsError] = useState(false);
  const [stateRetry, setStateRetry] = useState(0);
  const refRequest = useRef(0);
  const accountId = user?.id ?? null;

  // Identity changes and error-state retries go through a full reset (profile nulled, spinner
  // shown, error on failure); post-attach refreshes are silent and keep the rendered profile.
  const load = useCallback(
    ({ silent = false }: { silent?: boolean } = {}) => {
      refRequest.current += 1;
      if (!silent) {
        setStateProfile(null);
        setStateIsError(false);
      }
      if (!isAuthenticated || !rulebookId) {
        if (!silent) {
          setStateIsLoading(true);
        }
        return;
      }
      const request = refRequest.current;
      if (!silent) {
        setStateIsLoading(true);
      }
      getRulebookProfile(rulebookId)
        .then((profile) => {
          if (request === refRequest.current) {
            setStateProfile(profile);
          }
        })
        .catch(() => {
          if (request === refRequest.current && !silent) {
            setStateIsError(true);
          }
        })
        .finally(() => {
          if (request === refRequest.current && !silent) {
            setStateIsLoading(false);
          }
        });
    },
    [isAuthenticated, rulebookId],
  );

  useEffect(() => {
    load();
  }, [load, accountId, stateRetry]);

  const handleRetry = useCallback(() => {
    setStateRetry((previous) => previous + 1);
  }, []);

  const handleRefetch = useCallback(() => {
    load({ silent: true });
  }, [load]);

  if (isAuthLoading) {
    return <Spinner className="flex h-[50vh] items-center justify-center" data-testid="RulebookProfile-AuthSpinner" />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <PageMetaTags canonicalUrl={`/rulebook/${rulebookId}`} />
        <AnonymousMarketing datakey="creations" />
      </>
    );
  }

  if (stateIsLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center" data-testid="RulebookProfile-Loading">
        <Spinner />
      </div>
    );
  }

  if (stateIsError) {
    return (
      <div className="flex h-[50vh] items-center justify-center" data-testid="RulebookProfile-Error">
        <ErrorSection shouldCenter onClick={handleRetry} />
      </div>
    );
  }

  if (!stateProfile) {
    return (
      <div className="flex justify-center items-center h-full" data-testid="RulebookProfile-NotFound">
        <PageStaticMetaTags pageId="notFound" prerenderStatusCode={404} />
      </div>
    );
  }

  return (
    <div data-testid="RulebookProfile">
      <PageMetaTags title={stateProfile.name} canonicalUrl={`/rulebook/${stateProfile.id}`} />
      <PageLayout size={PageSize.Large} className="flex max-w-[1080px] flex-col gap-6">
        <RulebookProfileHeader profile={stateProfile} isDraftOwnerReady={isDraftStoreScoped} onRulebookAttached={handleRefetch} />
        <RulebookProfileSections profile={stateProfile} />
      </PageLayout>
    </div>
  );
};

export default RulebookProfile;
